/**
 * Rating bands (`EX-PUB23`, ADR-0081 decision 2; named and grown to ten by
 * `EX-BND01`, ADR-0083): the colour and the name a contestant's rating wears
 * on the leaderboard, the standings and their profile.
 *
 * The ladder is `docs/29` §7.6's table. One name per band, an English word in
 * Latin script in both languages (`docs/11` §2), read from the messages under
 * `judge.ratingBands.<band>`. The colours are tokens rather than hex literals
 * (rule 17): Tailwind palette shades and the brand's own, exactly the values
 * the decisions record. Apprentice is green-700, Solver to Advanced the 700
 * shades of ADR-0081, Master the brand orange (`primary-ink` for text,
 * `primary` for a fill), Grandmaster red-600, Legend rose-900 and Titan the
 * brand ramp's neutral-950 (`EX-BND02`, ADR-0129: Apprentice beside Solver
 * and Master beside Grandmaster read as the same colour). Titan additionally
 * wears a brand-orange mark before its name (`RatingBandName`).
 *
 * `null` is "unrated", the display rule every rating surface follows: no
 * colour, no name but "Unrated", never the 1200 a newcomer starts from.
 */
export type RatingBand =
  | "newcomer"
  | "apprentice"
  | "solver"
  | "expert"
  | "advanced"
  | "master"
  | "grandmaster"
  | "legend"
  | "titan";

export interface RatingBandEntry {
  band: RatingBand;
  /** The lowest rating the band takes; `null` for the bottom band. */
  from: number | null;
  /** The highest rating the band takes; `null` for the top band. */
  to: number | null;
}

/** The ladder from the bottom, each band touching the next. */
export const RATING_BANDS: ReadonlyArray<RatingBandEntry> = [
  { band: "newcomer", from: null, to: 1199 },
  { band: "apprentice", from: 1200, to: 1399 },
  { band: "solver", from: 1400, to: 1599 },
  { band: "expert", from: 1600, to: 1799 },
  { band: "advanced", from: 1800, to: 1999 },
  { band: "master", from: 2000, to: 2199 },
  { band: "grandmaster", from: 2200, to: 2599 },
  { band: "legend", from: 2600, to: 3199 },
  { band: "titan", from: 3200, to: null },
];

export function ratingBand(rating: number): RatingBand {
  for (let index = RATING_BANDS.length - 1; index >= 1; index -= 1) {
    const entry = RATING_BANDS[index]!;
    if (entry.from !== null && rating >= entry.from) return entry.band;
  }
  return "newcomer";
}

export function ratingBandEntry(band: RatingBand): RatingBandEntry {
  return RATING_BANDS.find((entry) => entry.band === band)!;
}

/**
 * ADR-0083 decision 3, the legend rule: the bands somebody holds plus the next
 * step. With only the highest rating held known (the leaderboard's page one is
 * rating-descending, so its first row is that), the legend runs from the
 * bottom of the ladder to the band of that rating and one band above it, never
 * the whole ladder as empty rows. `null` (nobody rated) lists the bottom two.
 */
export function legendBands(highestRating: number | null): ReadonlyArray<RatingBandEntry> {
  const top = highestRating === null ? 0 : RATING_BANDS.findIndex((entry) => entry.band === ratingBand(highestRating));
  return RATING_BANDS.slice(0, Math.min(RATING_BANDS.length, top + 2));
}

/**
 * The same rule for a chart: the bands a series crosses, from its lowest to
 * its highest point, plus the one above the highest.
 */
export function bandsCrossed(lowestRating: number, highestRating: number): ReadonlyArray<RatingBandEntry> {
  const low = RATING_BANDS.findIndex((entry) => entry.band === ratingBand(Math.min(lowestRating, highestRating)));
  const high = RATING_BANDS.findIndex((entry) => entry.band === ratingBand(Math.max(lowestRating, highestRating)));
  return RATING_BANDS.slice(low, Math.min(RATING_BANDS.length, high + 2));
}

/**
 * Which `judge.leaderboardPage` message prints a band's range, with its values:
 * `bandBelow` for the bottom band, `bandFrom` for the top, `bandRange` between.
 */
export function bandRangeMessage(entry: RatingBandEntry): {
  key: "bandBelow" | "bandFrom" | "bandRange";
  values: Record<string, number>;
} {
  // "below 1200", the next band's floor, not the bottom band's own ceiling of 1199.
  if (entry.from === null) return { key: "bandBelow", values: { to: (entry.to ?? -1) + 1 } };
  if (entry.to === null) return { key: "bandFrom", values: { from: entry.from } };
  return { key: "bandRange", values: { from: entry.from, to: entry.to } };
}

/** The band's text colour, for a name or a rating. */
export const BAND_TEXT: Readonly<Record<RatingBand, string>> = {
  newcomer: "text-neutral-500",
  apprentice: "text-green-700",
  solver: "text-teal-700",
  expert: "text-blue-700",
  advanced: "text-violet-700",
  master: "text-primary-ink",
  grandmaster: "text-red-600",
  legend: "text-rose-900",
  titan: "text-neutral-950",
};

/**
 * The same bands on the Judge's near-black (the leaderboard's podium), where a
 * 700 shade is too dark to read: the palette's 300 shade of the same hue.
 */
export const BAND_TEXT_ON_DARK: Readonly<Record<RatingBand, string>> = {
  newcomer: "text-neutral-400",
  apprentice: "text-green-300",
  solver: "text-teal-300",
  expert: "text-blue-300",
  advanced: "text-violet-300",
  master: "text-accent-peach",
  grandmaster: "text-red-400",
  legend: "text-rose-300",
  titan: "text-white",
};

/**
 * `BAND_TEXT_ON_DARK` as hex, for a surface Tailwind never reaches: the
 * profile's share card, drawn by `next/og` (`EX-SHR01`). The test resolves
 * each class against the palette, so the two cannot drift apart.
 */
export const BAND_HEX_ON_DARK: Readonly<Record<RatingBand, string>> = {
  newcomer: "#8A8A8A",
  apprentice: "#86EFAC",
  solver: "#5EEAD4",
  expert: "#93C5FD",
  advanced: "#C4B5FD",
  master: "#FFAB8F",
  grandmaster: "#F87171",
  legend: "#FDA4AF",
  titan: "#FFFFFF",
};

/** The band's fill, for an avatar's initial and a legend swatch. */
export const BAND_FILL: Readonly<Record<RatingBand, string>> = {
  newcomer: "bg-neutral-500",
  apprentice: "bg-green-700",
  solver: "bg-teal-700",
  expert: "bg-blue-700",
  advanced: "bg-violet-700",
  master: "bg-primary",
  grandmaster: "bg-red-600",
  legend: "bg-rose-900",
  titan: "bg-neutral-950",
};

/**
 * The band's stripe behind a rating chart (`EX-PRF02`): the same token as the
 * fill, drawn at low opacity by the chart so the line stays legible over it.
 */
export const BAND_STRIPE: Readonly<Record<RatingBand, string>> = {
  newcomer: "fill-neutral-500",
  apprentice: "fill-green-700",
  solver: "fill-teal-700",
  expert: "fill-blue-700",
  advanced: "fill-violet-700",
  master: "fill-primary",
  grandmaster: "fill-red-600",
  legend: "fill-rose-900",
  titan: "fill-neutral-950",
};

/**
 * The band's ring, the same token as the fill, around the avatar on the
 * profile's rail (`EX-PRF04`, ADR-0129).
 */
export const BAND_RING: Readonly<Record<RatingBand, string>> = {
  newcomer: "ring-neutral-500",
  apprentice: "ring-green-700",
  solver: "ring-teal-700",
  expert: "ring-blue-700",
  advanced: "ring-violet-700",
  master: "ring-primary",
  grandmaster: "ring-red-600",
  legend: "ring-rose-900",
  titan: "ring-neutral-950",
};

/** The text class for a rating that may be absent: nothing for "unrated". */
export function ratingTextClass(rating: number | null | undefined): string | undefined {
  return typeof rating === "number" ? BAND_TEXT[ratingBand(rating)] : undefined;
}
